// src/theme/index.js
import { extendTheme } from "@chakra-ui/react";

// 1. Temel Stil Tanımları (Foundations)
import colors from "./colors";        // Kendi brand ve alpha değerli renkleriniz burada.
import typography from "./typography";  // "Inter", "Roboto Mono" gibi modern fontlar.
import space from "./spacing";        // Varsayılan veya özel spacing ölçeğiniz.
import breakpoints from "./breakpoints";  // Varsayılan veya özel breakpoint'leriniz.
import radii from "./radii";          // Modern köşe yuvarlaklıkları.
import baseShadows from "./shadows";    // Temel gölge tanımları (lg, xl, outline vb.).

// Faz 1 · Genisletilmis token sistemi (yeni katmanlar; mevcutlari KIRMAZ).
import tokens, {
  paletteExtensions,
  semanticColors as extSemanticColors,
  semanticShadows as extSemanticShadows,
  typographyExtensions,
} from "./tokens";

// 2. Global Stiller
import styles from "./styles"; // body, @keyframes pulse, scrollbar stilleri.

// 3. Bileşen Tema Dosyaları (AI temasına uygun varyantlar içermeli)
import Button from "./components/button";
import Card from "./components/card";
import { Input, Select, Textarea } from "./components/input"; // Input, Select, Textarea için tek dosya
import { Form } from "./components/form"; // FormControl için
import Alert from "./components/alert";
import Badge from "./components/badge";
import Heading from "./components/heading"; // Mobil optimize heading
import Text from "./components/text"; // Mobil optimize text
import Container from "./components/container"; // Mobil optimize container
import Stack from "./components/stack"; // Mobil optimize stack
import Box from "./components/box"; // Mobil optimize box
import Tag from "./components/tag";
import Stat from "./components/stat";
import Tabs from "./components/tabs";
import Modal from "./components/modal";
import Link from "./components/link";
import Table from "./components/table";
import Kbd from "./components/kbd";
import Divider from "./components/divider";
import Skeleton from "./components/skeleton";
import { reactSelectStyles } from './components/reactSelect';
import Radio from "./components/radio";
import Progress from "./components/progress";
import Tooltip from "./components/tooltip";
import Menu from "./components/menu";

// 4. Tema Konfigürasyonu
const config = {
  initialColorMode: "system", // Ilk yuklemede kullanicinin OS tercihine uyum saglar
  // Faz 1 fix · Layout.jsx'teki manuel toggleColorMode butonu (header + mobile menu)
  // useSystemColorMode: true ile silently bypass ediliyordu. false yapinca: ilk yukleme
  // hala OS prefers-color-scheme'i takip eder (initialColorMode: 'system'), ancak
  // kullanicinin manuel toggle'i kalici (localStorage) ve sonraki ziyaretlerde aktif.
  useSystemColorMode: false,
};

// Faz 1 · Renk paletini yeni paletlerle birlestir.
// MEVCUT anahtarlar korunur (brand 50-900, gray, red, green vb. dokunulmaz).
// YENI anahtarlar eklenir: brand.25, brand.950, yeni alpha rampa,
// amber/teal/emerald/rose/slate/indigo paletleri.
const mergedColors = {
  ...colors,
  brand: {
    ...colors.brand,
    ...paletteExtensions.brand, // 25, 950, alpha rampa eklenir; 50-900 korunur
  },
  amber: paletteExtensions.amber,
  teal: paletteExtensions.teal,
  emerald: paletteExtensions.emerald,
  rose: paletteExtensions.rose,
  slate: paletteExtensions.slate,
  indigo: paletteExtensions.indigo,
};

// 5. Semantic Tokens (Anlamsal Renkler ve Gölgeler)
// Faz 1 NOT: extSemanticColors ve extSemanticShadows asagida sonuna spread edilir.
const semanticTokens = {
  colors: {
    // Sayfa ve Genel Arka Planlar - EKLENDİ: Çok koyu slate tema
    pageBackground: { default: "gray.50", _dark: "#1a1d29" }, // Ekran görüntüsü gibi çok koyu
    bgPrimary: { default: "whiteAlpha.900", _dark: "gray.800_85Alpha" },      // Kartlar - glassmorphism
    // bgCard: 40+ modul tarafindan dogrudan kullaniliyor (TopicCard, FlashcardViewer,
    // SRS bilesenleri, dashboard tab'lari, vb.) · bgPrimary'nin alias'i olarak korunur.
    bgCard: { default: "whiteAlpha.900", _dark: "gray.800_85Alpha" },         // Alias for bgPrimary
    bgSecondary: { default: "whiteAlpha.850", _dark: "gray.800_80Alpha" },    // İkincil alanlar
    bgTertiary: { default: "whiteAlpha.800", _dark: "gray.800_80Alpha" },     // Input arka planları
    bgOverlay: { default: "blackAlpha.700", _dark: "blackAlpha.900" },
    headerBg: { default: "whiteAlpha.900", _dark: "gray.800_90Alpha" },
    mobileDrawerBg: { default: "whiteAlpha.900", _dark: "gray.800_90Alpha" },

    // Metin Renkleri
    textPrimary: { default: "gray.800", _dark: "#f8f9fa" },
    textSecondary: { default: "gray.600", _dark: "#b8b9bd" },
    textMuted: { default: "gray.600", _dark: "#a0a1a4" },
    accent: { default: "brand.500", _dark: "brand.400" }, // Ana mavi buton rengi
    accentText: { default: "brand.700", _dark: "brand.200" },
    accentHover: { default: "brand.600", _dark: "brand.500" },
    accentActive: { default: "brand.700", _dark: "brand.600" },
    textOnAccent: { default: "white", _dark: "gray.900" },

    // Durum Renkleri - Success, Error, Warning, Info
    successBg: { default: "green.50", _dark: "green.900" },
    successIcon: { default: "green.500", _dark: "green.300" },
    successText: { default: "green.700", _dark: "green.200" },
    successBorder: { default: "green.200", _dark: "green.700" },
    
    errorBg: { default: "red.50", _dark: "red.900" },
    errorIcon: { default: "red.500", _dark: "red.300" },
    errorText: { default: "red.700", _dark: "red.200" },
    errorBorder: { default: "red.200", _dark: "red.700" },
    
    warningBg: { default: "yellow.50", _dark: "yellow.900" },
    warningIcon: { default: "yellow.500", _dark: "yellow.300" },
    warningText: { default: "yellow.700", _dark: "yellow.200" },
    warningBorder: { default: "yellow.200", _dark: "yellow.700" },
    
    infoBg: { default: "blue.50", _dark: "rgba(49, 130, 206, 0.15)" },
    infoIcon: { default: "blue.500", _dark: "blue.300" },
    infoText: { default: "blue.700", _dark: "blue.200" },
    infoBorder: { default: "blue.200", _dark: "blue.700" },

    // Danger Button Colors (for Profile page delete button)
    dangerBg: { default: "red.500", _dark: "red.400" },
    dangerHover: { default: "red.600", _dark: "red.500" },
    dangerActive: { default: "red.700", _dark: "red.600" },
    textOnDanger: { default: "white", _dark: "gray.900" },

    // Tab Colors
    tabSelectedText: { default: "white", _dark: "gray.900" },

    // Icon Colors
    iconColor: { default: "gray.500", _dark: "gray.400" },
    iconMuted: { default: "gray.400", _dark: "gray.500" },

    // Component Specific Colors
    // 'pageBg' ve 'componentBg' aliaslari kaldirildi · pageBackground / bgPrimary kanonik.
    // TODO: src/pages/MyFeedbackPage.jsx:94 hala 'componentBg' string'ini token olarak
    // okuyor ('const componentBg = "componentBg"'). O sayfa 'pageBackground' veya
    // 'bgPrimary' kullanacak sekilde guncellenmeli.
    headingColor: { default: "gray.700", _dark: "whiteAlpha.900" },
    linkColor: { default: "brand.500", _dark: "brand.300" },
    modalContentBg: { default: "whiteAlpha.900", _dark: "gray.800_85Alpha" },
    
    // Accent Alpha (highlight effects)
    accentAlpha: { default: "rgba(66, 153, 225, 0.1)", _dark: "rgba(99, 179, 237, 0.15)" },

    // AlgoWORD surfaces
    wordleTileIdleBg: { default: "whiteAlpha.900", _dark: "gray.800_60Alpha" },
    wordleTileIdleBorder: { default: "gray.300", _dark: "#4a5568" },
    wordleTileActiveBorder: { default: "brand.300", _dark: "brand.300" },
    wordleTileCorrectBg: { default: "green.500", _dark: "green.400" },
    wordleTilePresentBg: { default: "yellow.400", _dark: "yellow.300" },
    wordleTileAbsentBg: { default: "gray.500", _dark: "gray.650" },
    wordleTileSolvedText: { default: "white", _dark: "gray.900" },
    wordleKeyIdleBg: { default: "whiteAlpha.900", _dark: "gray.800_60Alpha" },
    wordleKeyIdleBorder: { default: "gray.300", _dark: "#363b4d" },
    wordleHintBg: { default: "blue.50", _dark: "var(--chakra-colors-blue-500_10Alpha)" },
    wordleHintBorder: { default: "blue.200", _dark: "blue.700" },
    wordleHintText: { default: "blue.700", _dark: "blue.200" },
    
    // Button Specific
    branchButtonBg: { default: "white", _dark: "gray.750" },
    branchButtonHoverBg: { default: "gray.100", _dark: "gray.600" },
    
    // Feature-specific accents (Flashcard purple, SRS purple, Coach blue)
    flashcardAccent: { default: "purple.600", _dark: "purple.400" },
    srsAccent: { default: "purple.600", _dark: "purple.400" },
    coachAccent: { default: "blue.500", _dark: "blue.300" },
    coachBg: { default: "blue.50", _dark: "blue.900" },
    coachBorder: { default: "blue.200", _dark: "blue.700" },
    coachText: { default: "blue.600", _dark: "blue.200" },
    
    // Flashcard Viewer specific
    flashcardFrontGradient: { default: "linear-gradient(135deg, #9F7AEA 0%, #805AD5 100%)", _dark: "linear-gradient(135deg, #553C9A 0%, #44337A 100%)" },
    flashcardBackGradient: { default: "linear-gradient(135deg, #D6BCFA 0%, #B794F4 100%)", _dark: "linear-gradient(135deg, #44337A 0%, #322659 100%)" },
    flashcardOverlayBg: { default: "rgba(255,255,255,0.95)", _dark: "rgba(45,55,72,0.95)" },
    flashcardTagBg: { default: "whiteAlpha.400", _dark: "whiteAlpha.300" },
    flashcardHeadingFront: { default: "white", _dark: "purple.200" },
    flashcardHeadingBack: { default: "white", _dark: "purple.300" },
    flashcardBookmarkColor: { default: "yellow.500", _dark: "yellow.400" },
    
    // Quiz Setup Interface - Radio Card Backgrounds
    quizModeSelectedBg: { default: "brand.50", _dark: "rgba(66, 153, 225, 0.15)" },
    // quizModeSelectedBorder: semantic→semantic alias yerine palet referansi
    quizModeSelectedBorder: { default: "brand.500", _dark: "brand.400" },
    denemeModeSelectedBg: { default: "purple.50", _dark: "rgba(159, 122, 234, 0.15)" },
    denemeModeSelectedBorder: { default: "purple.500", _dark: "purple.500" },
    quizFilterSummaryBg: { default: "brand.50", _dark: "rgba(66, 153, 225, 0.15)" },
    quizFilterSummaryBgDeneme: { default: "purple.50", _dark: "rgba(159, 122, 234, 0.15)" },
    flashcardFilterSummaryBg: { default: "purple.50", _dark: "rgba(159, 122, 234, 0.15)" },
    
    // Table Row Hover
    tableRowHoverBg: { default: "gray.50", _dark: "gray.700" },
    tableHeaderBg: { default: "gray.100", _dark: "gray.750" },
    
    // Stat Box Background
    statBoxBg: { default: "gray.50", _dark: "gray.800" },

    // Kenarlık Renkleri
    borderPrimary: { default: "gray.200", _dark: "#363b4d" },
    borderSecondary: { default: "gray.300", _dark: "#2d3142" },
    // borderAccent: semantic→semantic alias yerine palet referansi
    // (Chakra v2 nested semantic resolution edge-case'lerini onler).
    borderAccent: { default: "brand.500", _dark: "brand.400" },

    // Input Alanları - Koyu tema için daha belirgin
    inputBg: { default: 'whiteAlpha.800', _dark: 'gray.800_60Alpha' },
    inputBorder: { default: "borderPrimary", _dark: "#363b4d"},
    inputPlaceholder: { default: 'gray.400', _dark: '#6b6d75' },
    // inputFocusBorder: semantic→semantic alias yerine palet referansi
    inputFocusBorder: { default: "brand.500", _dark: "brand.400" },

    // Durum Renkleri
    statusError: { default: "red.500", _dark: "red.400" },
    statusSuccess: { default: "green.500", _dark: "green.400" },
    statusWarning: { default: "yellow.400", _dark: "yellow.300" },
    statusInfo: { default: "blue.500", _dark: "blue.300" },

    textStatusSuccess: { default: "green.600", _dark: "green.300" },
    textStatusError: { default: "red.600", _dark: "red.300" },

    // Quiz Şık Renkleri
    optionDefaultBg: { default: "gray.50", _dark: "#2d3142" },
    optionDefaultText: { default: "textSecondary", _dark: "textSecondary" },
    optionDefaultBorderColor: { default: "borderPrimary", _dark: "borderPrimary"},
    optionSelectedBg: { default: "yellow.300", _dark: "yellow.500" },
    optionSelectedText: { default: "gray.900", _dark: "gray.900" },
    optionSelectedBorderColor: { default: "yellow.400", _dark: "yellow.600"},
    optionCorrectBg: { default: "green.400", _dark: "green.600" },
    optionCorrectText: { default: "white", _dark: "white" },
    optionCorrectBorderColor: { default: "green.500", _dark: "green.700"},
    optionIncorrectBg: { default: "red.400", _dark: "red.600" },
    optionIncorrectText: { default: "white", _dark: "white" },
    optionIncorrectBorderColor: { default: "red.500", _dark: "red.700"},
    optionEliminatedOpacity: { default: "0.6", _dark: "0.55" },
    optionEliminatedText: { default: "gray.500", _dark: "gray.400"},
    optionEliminatedBg: { default: "gray.200", _dark: "gray.650" },

    // Navigasyon
    navLinkActiveColor: { default: "brand.600", _dark: "brand.300" },
    navLinkHoverBg: { default: "gray.100", _dark: "#2d3142" },
    mobileNavLinkActiveBg: { default: "brand.50", _dark: "brand.800_30Alpha" },
    mobileNavLinkActiveColor: { default: "brand.600", _dark: "brand.300" },
    iconButtonHoverBg: { default: "gray.200", _dark: "#363b4d" },
    menuItemBackgroundError: { default: "red.50", _dark: "var(--chakra-colors-red-500_alpha_12)"},
    notificationItemUnreadBg: { default: "blue.50", _dark: "var(--chakra-colors-blue-500_10Alpha)"},

    // Özel Kullanım
    iconDecorativeYellow: { default: "yellow.400", _dark: "yellow.300" },
    bgExplanation: { default: "blue.50", _dark: "var(--chakra-colors-blue-500_15Alpha)" },
    borderExplanation: { default: "blue.200", _dark: "blue.700" },
    textExplanationHeading: { default: "blue.700", _dark: "blue.200" },
    textExplanationBody: { default: "gray.700", _dark: "gray.300" },
    
    // Chart.js Renkleri
    chartPrimary: { default: "#4F3BA9", _dark: "#9F7AEA" },
    chartPrimaryFaint: { default: "rgba(79,59,169,0.15)", _dark: "rgba(159,122,234,0.25)" },
    chartPrimaryBorder: { default: "rgba(79, 59, 169, 0.7)", _dark: "rgba(159, 122, 234, 0.9)" },
    chartPrimaryBg: { default: "rgba(79, 59, 169, 0.15)", _dark: "rgba(159, 122, 234, 0.3)" },
    chartSecondary: { default: "rgba(255,159,64,0.25)", _dark: "rgba(255,159,64,0.35)" },
    chartSecondaryFull: { default: "#E2E8F0", _dark: "#4A5568" }, // Doughnut chart için opak gri
    chartTeal: { default: "#38B2AC", _dark: "#4FD1C5" }, // Teal/Turkuaz
    chartOrange: { default: "#ED8936", _dark: "#F6AD55" }, // Turuncu
    chartGrid: { default: "rgba(0,0,0,0.08)", _dark: "rgba(255,255,255,0.08)" },
    chartAxis: { default: "#4A5568", _dark: "#A0AEC0" },
    chartTooltipBg: { default: "white", _dark: "gray.700" },
    chartPointBorder: { default: "white", _dark: "gray.700" },
    mentorChartPrimary: { default: "#3182CE", _dark: "#63B3ED" },
    mentorChartPrimaryBg: { default: "rgba(49,130,206,0.12)", _dark: "rgba(99,179,237,0.24)" },
    mentorChartPrimaryStrongBg: { default: "rgba(49,130,206,0.18)", _dark: "rgba(99,179,237,0.3)" },
    mentorChartSecondary: { default: "#805AD5", _dark: "#B794F4" },
    mentorChartSecondaryBg: { default: "rgba(128,90,213,0.15)", _dark: "rgba(183,148,244,0.25)" },
    mentorChartOrange: { default: "#DD6B20", _dark: "#F6AD55" },
    mentorChartOrangeBg: { default: "rgba(221,107,32,0.12)", _dark: "rgba(246,173,85,0.24)" },
    mentorChartGreen: { default: "#38A169", _dark: "#68D391" },
    mentorChartRed: { default: "#E53E3E", _dark: "#FC8181" },
    mentorChartGrid: { default: "rgba(0,0,0,0.05)", _dark: "rgba(255,255,255,0.05)" },
    mentorChartAxis: { default: "#4A5568", _dark: "#A0AEC0" },
    mentorChartTooltipBg: { default: "white", _dark: "#2D3748" },
    scrollbarThumb: { default: "gray.300", _dark: "gray.600" },

    // Faz 4.1 · Menu component icin semantic token'lar.
    // Eski menu.js icindeki '#252837' / '#363b4d' / '#2d3142' / vb. token'a tasindi.
    menuBg:                 { default: "white",        _dark: "slate.750" },
    menuItemHoverBg:        { default: "gray.100",     _dark: "whiteAlpha.100" },
    menuItemHoverBgSubtle:  { default: "gray.50",      _dark: "whiteAlpha.50" },
    menuItemActiveBg:       { default: "gray.200",     _dark: "whiteAlpha.200" },
    menuScrollbarThumb:      { default: "blackAlpha.300", _dark: "whiteAlpha.300" },
    menuScrollbarThumbHover: { default: "blackAlpha.500", _dark: "whiteAlpha.500" },

    // ============================================================
    // Faz 1 · Genisletilmis Semantic Color Token'lari
    // exam.* / srs.* / chart.* / gradient.* / marketing.* /
    // reading.* / state.* / surface.* namespace'leri
    // ============================================================
    ...extSemanticColors,
  },
  // ==============================================================
  // Faz 5 · Merkezi icon boyut sistemi
  // Tum sayfalar bunu kullanmali: <Icon boxSize="iconMd" />
  //   iconXs  = 12px  (inline mikro rozetler)
  //   iconSm  = 16px  (sikisik listeler, form iconlari)
  //   iconMd  = 20px  (varsayilan list/menu/sidebar iconlari)
  //   iconLg  = 24px  (baslik / section header iconlari)
  //   iconXl  = 40px  (empty state / buyuk vurgular)
  //   iconHero = 64px (splash / hero / welcome)
  // ==============================================================
  sizes: {
    iconXs:   "12px",
    iconSm:   "16px",
    iconMd:   "20px",
    iconLg:   "24px",
    iconXl:   "40px",
    iconHero: "64px",
  },
  shadows: {
    // Bu anlamsal gölgeler, './shadows.js' (baseShadows) dosyanızda tanımlı olan
    // temel gölge anahtarlarını (örn: 'lg', 'xl') kullanır.
    cardElevated: { default: "lg", _dark: "xl" }, // baseShadows.lg ve baseShadows.xl kullanılır
    // AI teması için "glow" efekti (colors.js'de alpha değerli brand renkleri tanımlanmalı)
    "aiGlowAccentSm": {
      default: `0 0 8px 0px var(--chakra-colors-brand-500-alpha-20)`,
      _dark: `0 0 12px 1px var(--chakra-colors-brand-300-alpha-30)`
    },
    "aiGlowAccentMd": {
      default: `0 0 12px 1px var(--chakra-colors-brand-500-alpha-25)`,
      _dark: `0 0 18px 2px var(--chakra-colors-brand-300-alpha-35)`
    },
    // Odak çizgileri (colors.js'de alpha değerli brand ve red renkleri tanımlanmalı)
    focusOutlineAccent: { // focusOutlineBlue yerine daha genel
      default: `0 0 0 3px var(--chakra-colors-brand-500-alpha-40)`,
      _dark: `0 0 0 3px var(--chakra-colors-brand-300-alpha-50)`
    },
    focusOutlinePurple: {
      default: `0 0 0 3px rgba(159, 122, 234, 0.4)`,
      _dark: `0 0 0 3px rgba(159, 122, 234, 0.5)`
    },
    focusOutlineError: {
      default: `0 0 0 3px var(--chakra-colors-red-500-alpha-40)`,
      _dark: `0 0 0 3px var(--chakra-colors-red-300-alpha-50)`
    },
    // Eğer './shadows.js' içinde 'outline' anahtarı varsa, 'focusOutlineBlue' onu kullanır.
    // focusOutlineBlue: { default: "outline", _dark: "outline" },

    // ============================================================
    // Faz 1 · Elevation hiyerarsisi + tutarli focusRing token'lari
    // elevation.0..5, elevation.glow.*, focusRing.*
    // ============================================================
    ...extSemanticShadows,

    // Faz 4.1 · Menu shadow token'lari (eski inline rgba'lar)
    menuShadow: {
      default: '0 10px 40px -10px rgba(0,0,0,0.15), 0 4px 12px -2px rgba(0,0,0,0.08)',
      _dark:   '0 10px 50px -10px rgba(0,0,0,0.5), 0 4px 20px -2px rgba(0,0,0,0.3)',
    },
    menuShadowUp: {
      default: '0 -10px 40px -10px rgba(0,0,0,0.15)',
      _dark:   '0 -10px 50px -10px rgba(0,0,0,0.5)',
    },
    menuShadowStrong: {
      default: '0 10px 40px -10px rgba(0,0,0,0.2), 0 4px 16px -4px rgba(0,0,0,0.1)',
      _dark:   '0 10px 50px -10px rgba(0,0,0,0.6), 0 4px 24px -4px rgba(0,0,0,0.4)',
    },
  }
};

// Faz 1 · Tipografi extension'lari (mevcut typography.js uzerine deep-merge).
// Mevcut Inter / Roboto Mono ve mevcut fontSizes/lineHeights/letterSpacings
// AYNEN korunur; yeni 'reading' (serif), 'numeric', 'display' family'leri,
// 'display.sm/md/lg/xl/2xl' fontSize'lari, 'display'/'heading'/'body' letterSpacing
// semantic adlari, 'reading'/'body'/'ui' lineHeight'lari eklenir.
const mergedTypography = {
  fonts:           { ...typography.fonts,           ...typographyExtensions.fonts },
  fontSizes:       { ...typography.fontSizes,       ...typographyExtensions.fontSizes },
  letterSpacings:  { ...typography.letterSpacings,  ...typographyExtensions.letterSpacings },
  lineHeights:     { ...typography.lineHeights,     ...typographyExtensions.lineHeights },
  fontWeights:     { ...typography.fontWeights,     ...typographyExtensions.fontWeights },
};

// 6. Tema Geçersiz Kılmaları (Overrides)
const overrides = {
  config,         // Renk modu konfigürasyonu
  styles,         // Global stiller
  colors: mergedColors, // Faz 1: brand genisletildi + amber/teal/emerald/rose/slate/indigo eklendi
  shadows: {      // Temel gölge tanımları
    ...baseShadows, // ./shadows.js'den gelen lg, xl, outline vb.
    // Özel gölgeler (aiGlowAccentSm gibi) semanticTokens.shadows altında tanımlandığı için
    // burada tekrar tanımlamaya gerek yok. Onları doğrudan bileşenlerde semantic token olarak kullanacağız.
  },
  radii,          // Köşe yuvarlaklıkları
  space,          // Boşluk ölçeği
  breakpoints,    // Ekran kırılma noktaları
  ...mergedTypography, // Faz 1: Mevcut + display scale + reading serif + semantic letterSpacings/lineHeights
  semanticTokens, // YUKARIDA TANIMLANAN ANLAMSAL TOKENSLAR
  components: {   // Bileşenlere özel tema ayarları
    Button,     // ./components/button.js'den gelir
    Card,       // ./components/card.js'den gelir
    Input,      // ./components/input.js'den gelir (Input için)
    Select,     // ./components/input.js'den gelir (Select için)
    Textarea,   // ./components/input.js'den gelir (Textarea için)
    Form,       // ./components/form.js'den gelir (FormControl için)
    Alert,      // ./components/alert.js'den gelir
    Badge,      // ./components/badge.js'den gelir
    Radio,      // ./components/radio.js'den gelir
    Progress,   // ./components/progress.js'den gelir
    Tooltip,   // ./components/tooltip.js'den gelir
    Heading,    // ./components/heading.js'den gelir - Mobil optimize
    Text,       // ./components/text.js'den gelir - Mobil optimize
    Container,  // ./components/container.js'den gelir - Mobil optimize
    Stack,      // ./components/stack.js'den gelir - Mobil optimize
    Box,        // ./components/box.js'den gelir - Mobil optimize
    Tag,        // ./components/tag.js'den gelir
    Stat,       // ./components/stat.js'den gelir
    ReactSelect: reactSelectStyles,
    Tabs,
    Menu,
    Modal,
    Table,
    Link,
    Kbd,
    Divider,
    Skeleton,
  },
};

export default extendTheme(overrides);
